import React from 'react';
import type { ApiResponse } from '@/api/handlers/responses';
import { resolvePinError, type PinError } from './pinPadMeta';

/**
 * What a PIN screen shows for the last failed attempt. A rate limit carries the
 * seconds still to wait, ticking down once a second.
 */
export type PinAttemptError =
    | { kind: 'lockout' }
    | { kind: 'rateLimited'; secondsLeft: number }
    | { kind: 'invalid' };

/** A localized message for a failed attempt: an i18n key and its values. */
export type PinErrorMessage = {
    key: string;
    values?: Record<string, number>;
};

/** Each screen's own i18n keys for the three kinds of failure. */
export type PinErrorKeys = Record<PinAttemptError['kind'], string>;

/** The message a PIN screen shows for `error`, in that screen's own words. */
export const pinAttemptMessage = (
    error: PinAttemptError | null,
    keys: PinErrorKeys,
): PinErrorMessage | null => {
    if (!error) {
        return null;
    }
    return error.kind === 'rateLimited'
        ? { key: keys.rateLimited, values: { seconds: error.secondsLeft } }
        : { key: keys[error.kind] };
};

type Countdown = { source: PinError | null; secondsLeft: number };

/**
 * Classifies a failed PIN answer by its code (BOS-190) and, for `RATE_LIMITED`,
 * counts down the server's `Retry-After`. Once the wait is over the error clears
 * to `null`, so the pad comes back without the operator doing anything.
 *
 * Shared by the seller login and the shift co-sign. Pass the mutation's `data`:
 * each new answer restarts the countdown, even one with the same delay.
 */
export const usePinAttemptError = (
    result: ApiResponse<unknown> | undefined,
): PinAttemptError | null => {
    const failure = React.useMemo<PinError | null>(
        () =>
            result && !result.success ? resolvePinError(result.error) : null,
        [result],
    );
    const retryAfterSeconds =
        failure?.kind === 'rateLimited' ? failure.retryAfterSeconds : null;

    const [countdown, setCountdown] = React.useState<Countdown>({
        source: null,
        secondsLeft: 0,
    });

    React.useEffect(() => {
        if (retryAfterSeconds === null) {
            return;
        }
        const deadline = Date.now() + retryAfterSeconds * 1000;
        const timer = setInterval(() => {
            const secondsLeft = Math.max(
                0,
                Math.ceil((deadline - Date.now()) / 1000),
            );
            setCountdown({ source: failure, secondsLeft });
            if (secondsLeft === 0) {
                clearInterval(timer);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [failure, retryAfterSeconds]);

    if (!failure) {
        return null;
    }
    if (failure.kind !== 'rateLimited') {
        return failure;
    }
    // Until the first tick for this answer, the full delay is what is left.
    const secondsLeft =
        countdown.source === failure
            ? countdown.secondsLeft
            : failure.retryAfterSeconds;
    return secondsLeft > 0 ? { kind: 'rateLimited', secondsLeft } : null;
};
